import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';

interface BurnSettings {
  burnFee: string;
  collectors: { address: string; percentage: number; }[];
}

interface BurnSettingsContextType {
  settings: BurnSettings;
  isLoading: boolean;
}

const DEFAULT_SETTINGS: BurnSettings = {
  burnFee: "0.125",
  collectors: [{
    address: "0x760A0D5cC0ca242eB83609C627AB00Ac3F726Ba7",
    percentage: 100
  }]
};

const BurnSettingsContext = createContext<BurnSettingsContextType | undefined>(undefined);

export function BurnSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<BurnSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, "settings", "fees"),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          // Get the BSC network fees (chainId 56)
          const bscFees = data.networkFees?.[56] || DEFAULT_SETTINGS;
          setSettings({
            burnFee: bscFees.burnFee || DEFAULT_SETTINGS.burnFee,
            collectors: data.collectors?.map(c => ({
              address: c.address,
              percentage: c.share
            })) || DEFAULT_SETTINGS.collectors
          });
        }
        setIsLoading(false);
      },
      (error) => {
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return (
    <BurnSettingsContext.Provider value={{ settings, isLoading }}>
      {children}
    </BurnSettingsContext.Provider>
  );
}

export function useBurnSettings() {
  const context = useContext(BurnSettingsContext);
  if (context === undefined) {
    throw new Error('useBurnSettings must be used within a BurnSettingsProvider');
  }
  return context;
}
